import React, { useState, useEffect } from 'react';

import { StackableModal } from '../components/nextgen';
import { TemplateField, TemplateReactSelect } from '../components/Attributes/Attributes_old';
import Button from '../components/Button';

import { addContact, getContacts } from '../api/contacts';

import useRequired from '../hooks/useRequired';
import useModal from '../hooks/useModal';
import useAsync from '../hooks/useAsync';

const MODAL_NAME = 'addVendorModal';

const QuickAddVendor = () => {
	const [formVendor, setFormVendor] = useState({ type: 'company' });

	const { open: options, setClose } = useModal(MODAL_NAME);

	const { execute: onAddVendor, status: isAddingVendor } = useAsync(
		addContact,
		{ immediate: false }
	);

	// Fetch companies for dropdown
	const { value: companies, execute: fetchCompanies } = useAsync(
		getContacts,
		{ immediate: false }
	);

	useEffect(() => {
		if (options) {
			// Fetch companies when modal opens
			fetchCompanies({ type: 'company', pageSize: null });
		}
	}, [options, fetchCompanies]);

	const contactType = formVendor.type || 'company';

	const getRequiredFields = () => {
		return contactType === 'company' ? ['companyName'] : ['firstName', 'lastName'];
	};

	const getLabel = () => {
		if (contactType === 'company') {
			return formVendor.companyName;
		} else {
			return [formVendor.firstName, formVendor.lastName]
				.filter(Boolean)
				.join(' ');
		}
	};

	// Convert stored companyId back to option format for display
	const selectedCompanyOption = formVendor.companyId
		? companies?.rows?.find(c => c.id === formVendor.companyId)
			? {
				label: companies.rows.find(c => c.id === formVendor.companyId).companyName,
				value: formVendor.companyId
			}
			: null
		: null;

	return (
		<StackableModal title="Quick Add Vendor" open={!!options} onClose={() => setClose()} size="md">
			{/* Type Selector */}
			<div className="mb-4">
				<label className="block text-sm font-medium text-gray-700 mb-2">
					Type
				</label>
				<div className="flex gap-4">
					<label className="flex items-center cursor-pointer">
						<input
							type="radio"
							name="contactType"
							value="company"
							checked={contactType === 'company'}
							onChange={(e) =>
								setFormVendor({ type: e.target.value })
							}
							className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
						/>
						<span className="ml-2 text-sm text-gray-700">Company</span>
					</label>
					<label className="flex items-center cursor-pointer">
						<input
							type="radio"
							name="contactType"
							value="person"
							checked={contactType === 'person'}
							onChange={(e) =>
								setFormVendor({ type: e.target.value })
							}
							className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
						/>
						<span className="ml-2 text-sm text-gray-700">Individual</span>
					</label>
				</div>
			</div>

			<div className="grid grid-cols-2 gap-y-4 gap-x-4 text-left">
				{contactType === 'company' ? (
					<>
						<TemplateField
							name="Company Name"
							required
							value={formVendor.companyName}
							onChange={(i, name) =>
								setFormVendor({ ...formVendor, companyName: name })
							}
						/>
						<TemplateField
							name="Email"
							value={formVendor.email}
							onChange={(i, email) => setFormVendor({ ...formVendor, email })}
						/>
						<TemplateField
							name="Phone Number"
							value={formVendor.phone}
							onChange={(i, phone) => setFormVendor({ ...formVendor, phone })}
						/>
						<TemplateField
							name="URL"
							value={formVendor.url}
							onChange={(i, url) => setFormVendor({ ...formVendor, url })}
						/>
					</>
				) : (
					<>
						<TemplateField
							name="First Name"
							required
							value={formVendor.firstName}
							onChange={(i, firstName) =>
								setFormVendor({ ...formVendor, firstName })
							}
						/>
						<TemplateField
							name="Last Name"
							required
							value={formVendor.lastName}
							onChange={(i, lastName) =>
								setFormVendor({ ...formVendor, lastName })
							}
						/>
						<TemplateField
							name="Email"
							value={formVendor.email}
							onChange={(i, email) => setFormVendor({ ...formVendor, email })}
						/>
						<TemplateField
							name="Phone Number"
							value={formVendor.phone}
							onChange={(i, phone) => setFormVendor({ ...formVendor, phone })}
						/>
						<TemplateField
							name="Title"
							value={formVendor.title}
							onChange={(i, title) => setFormVendor({ ...formVendor, title })}
						/>
						<TemplateReactSelect
							name="Company"
							value={selectedCompanyOption}
							options={
								companies?.rows
									?.sort((a, b) => {
										const nameA = a.companyName || '';
										const nameB = b.companyName || '';
										return nameA.localeCompare(nameB, undefined, {
											sensitivity: 'base',
										});
									})
									.map((company) => ({
										label: company.companyName,
										value: company.id,
									})) || []
							}
							onChange={(i, selected) =>
								setFormVendor({ ...formVendor, companyId: selected?.value || selected })
							}
							placeholder="Search companies..."
							isClearable
						/>
					</>
				)}
			</div>
			<div className="flex justify-end mt-6">
				<Button
					onClick={() => {
						onAddVendor(formVendor, (response) => {
							if (typeof options?.onSubmit === 'function') {
								options?.onSubmit({
									label: getLabel(),
									value: response.id,
									response,
								});
								setFormVendor({ type: 'company' });
							}
							setClose();
						});
					}}
					disabled={!useRequired(formVendor, getRequiredFields())}
				>
					{isAddingVendor === 'pending' ? 'Adding Vendor' : 'Add Vendor'}
				</Button>
			</div>
		</StackableModal>
	);
};

export default QuickAddVendor;
